import { useEffect, useMemo, useState } from 'react';
import { max, min } from '@visx/vendor/d3-array';
import * as d3Color from '@visx/vendor/d3-color';
import { AreaStack, Axis, BarGroup, BarStack, DataProvider, Grid, TooltipProvider, XYChart } from '@visx/xychart';
import { mapValues, orderBy, pick, uniqBy } from 'lodash';
import { pipe } from 'lodash/fp';

import { getExecutionColumnType } from '@blockworks/platform/api/research/studio/execution';
import { GroupConfig } from '@blockworks/platform/api/research/studio/visualizations';
import { useEvent } from '@blockworks/platform/hooks';
import { Bit } from '@blockworks/ui/bit';
import { createTheme } from '@blockworks/ui/charts/utils';

import { useVisualizationOptionsContext } from '../../../../../context';
import { maxRowsPerInterval, useResponsiveChart } from '../../../utils';
import { VisualizationTooltip } from '../tooltip';
import { formats, formatTick } from '../utils';

import { BrushArea, useIsTouchDevice } from './brush';
import { Legend } from './legend';
import {
    BRUSH_HEIGHT,
    calculateMargin,
    createConvertor,
    seriesAxisConfig,
    TICK_LABEL_CLASSNAME,
    TimeseriesComponents,
    type TimeseriesProps,
} from './timeseries.model';

export function Timeseries<T extends {}>({ id: vizId, data: rawData, options, params, meta }: TimeseriesProps) {
    const { containerRef, width, height } = useResponsiveChart();

    const {
        xAxis,
        brush,
        logarithmic,
        groups: rawGroups = [] as GroupConfig<T>[],
        legend,
        tooltip,
        strokeWidth = 1.5,
    } = options;

    const isLikelyTouchDevice = useIsTouchDevice();
    const brushEnabled = !isLikelyTouchDevice && brush;

    const activeIntervalUnit = useMemo(() => {
        const baseInterval = meta?.dimensions[xAxis.column as keyof typeof meta]?.unit;
        return params?.timeframe ?? baseInterval;
    }, [params?.timeframe, xAxis.column, meta]);

    /** TODO: Need to do something else here with the new chart internals - we can't accurately guess the visible xValues
     * and brush updates will pingpong.
     */
    const [bounds, setBrushBounds] = useState<{ start: number; end: number } | undefined>(() => {
        return { start: 0, end: 0 };
    });

    const [highlight, setHighlight] = useState<keyof T | undefined>(undefined);
    const { layout } = useVisualizationOptionsContext();
    const canShowLegend = !layout?.hideLegend;
    const [hidden, setHidden] = useState([] as Array<keyof T>);

    const isSmallArea = width < 500;

    // If any of the column groups change force reset of hidden
    const shouldResetHidden = rawGroups.flatMap(group => group.series.map(series => series.column)).join('-');

    useEffect(() => {
        setHidden(prev => (prev.length === 0 ? prev : []));
    }, [shouldResetHidden]);

    const dynamicLegend = useMemo(() => {
        if (!legend) return legend;
        return {
            ...legend,
            position: isSmallArea && ['right', 'left'].includes(legend?.position) ? 'bottom' : legend?.position,
        };
    }, [isSmallArea, legend]);

    const { orderedData, reverse } = useMemo(() => {
        let parsedData = rawData.map(row => {
            let xColumnValue = row[xAxis.column];
            if (xAxis.type === 'date') {
                xColumnValue = new Date(row[xAxis.column]);
            } else {
                xColumnValue = String(row[xAxis.column]);
            }

            const baseRow = {
                ...row,
                [xAxis.column]: xColumnValue,
            };
            return baseRow;
        });

        const overrideDirection = xAxis.orderDirection ?? 'desc';

        if (xAxis.type === 'enum') {
            const orderByColumnName = xAxis.orderBy ?? xAxis.column;
            const isDefaultOrderStrategy = orderByColumnName === xAxis.column;
            if (isDefaultOrderStrategy) {
                return {
                    orderedData: parsedData,
                    reverse: overrideDirection === 'desc',
                };
            }

            const orderByColumnType = getExecutionColumnType(meta?.columnTypes[orderByColumnName]);

            if (orderByColumnType === 'number') {
                parsedData = parsedData.map(d => ({
                    ...d,
                    [orderByColumnName]: +d[orderByColumnName],
                }));
            }
            return {
                orderedData: orderBy(parsedData, [orderByColumnName], [overrideDirection]),
                reverse: false,
            };
        }

        return { orderedData: parsedData, reverse: true };
    }, [rawData, xAxis.orderDirection, xAxis.orderBy, xAxis.column, xAxis.type, meta?.columnTypes]);

    const maximumMobileRows = useMemo(() => {
        const approximateInterval = (params?.timeframe as keyof typeof maxRowsPerInterval) ?? 'days';
        return Math.min(maxRowsPerInterval[approximateInterval]!, orderedData.length - 1);
    }, [params?.timeframe, orderedData.length]);

    const filteredData = useMemo(() => {
        if (!brush) return orderedData;
        if (brush && isLikelyTouchDevice) return orderedData.slice(0, maximumMobileRows);
        if (!Number.isInteger(bounds?.start) || !Number.isInteger(bounds?.end)) return orderedData;
        const summaryData = orderedData.slice(bounds?.end, bounds?.start);
        return summaryData;
    }, [bounds?.start, bounds?.end, maximumMobileRows, brush, isLikelyTouchDevice, orderedData]);

    const { groups, parsedData } = useMemo(() => {
        const groupScales = rawGroups.map(({ series, ...rest }) => {
            const cols = series.flatMap(s => s.column);
            const lMin = min(filteredData, d => min(cols.map(col => Number(d[col])))) ?? 0;
            const lMax = max(filteredData, d => max(cols.map(col => Number(d[col])))) ?? 0;
            return { ...rest, series, cols, min: lMin, max: lMax, delta: lMax - lMin };
        });

        const createLocalRange = (scales: typeof groupScales, orientation: 'left' | 'right' | 'none') => {
            if (orientation === 'none') return {};
            const axisScales = scales.filter(scale => scale.axis.orientation === orientation);
            const lMin = min(axisScales, scale => scale.min) ?? 0;
            const lMax = max(axisScales, scale => scale.max) ?? 0;
            return { min: lMin, max: lMax, delta: lMax - lMin };
        };

        // Do a second pass to pin the scales to assigned axis as the first pass is specific to series types and will not work for all cases
        const localizedGroupScales = groupScales.map(scale => {
            return { ...scale, ...createLocalRange(groupScales, scale.axis.orientation) };
        });

        const primaryIniital = localizedGroupScales[0];

        const primary = {
            min: primaryIniital?.min ?? 0,
            max: primaryIniital?.max ?? 0,
            delta: primaryIniital?.delta ?? 0,
        };

        const converterGroups = localizedGroupScales.map(scale => {
            const format = formats[scale.axis.format ?? 'number'];
            const toPrimary = createConvertor(primary, scale);
            const fromPrimary = createConvertor(scale, primary);
            const shouldRespectOrder = scale.stack && ['area', 'bar'].includes(scale.type);
            const series = shouldRespectOrder ? [...scale.series].reverse() : [...scale.series];

            return {
                ...scale,
                series,
                toPrimary,
                fromPrimary,
                tooltipFormat: pipe(fromPrimary, format.tooltip),
                tickFormat: pipe(fromPrimary, format.tick),
            };
        });
        const parsedData = filteredData.map(row => {
            const rowGroups = converterGroups.map(scale => {
                return mapValues(pick(row, scale?.cols ?? []), col => {
                    if (col === null) return null;
                    return scale?.toPrimary?.(col);
                });
            });

            return rowGroups.reduce((acc, next) => Object.assign({}, acc, next), { ...row });
        });

        return { groups: converterGroups, parsedData };
    }, [filteredData, rawGroups]);

    const axisStyleType = groups.some(g => g.type === 'bar') ? 'bar' : 'base';
    const { xAxis: xScaleConfig } = seriesAxisConfig[axisStyleType];
    const numYTicks = logarithmic ? 3 : Math.floor(height / 60);

    const { numXTicks, tickLength, tickWidth } = useMemo(() => {
        let tickBandSize = xAxis.tickStrategy === 'all' ? 20 : 100;
        if (xAxis.type === 'date') {
            return { numXTicks: Math.floor(width / tickBandSize), tickWidth: 20, tickLength: 100 };
        }

        const maxTickLength = Math.max(...orderedData.map(d => d[xAxis.column].length)) * 8;
        const tickLengthAdjusted = maxTickLength / 3;
        tickBandSize = xAxis.tickStrategy === 'all' ? 20 : maxTickLength + 4;

        return { numXTicks: Math.floor(width / tickBandSize), tickWidth: 20, tickLength: tickLengthAdjusted };
    }, [orderedData, width, xAxis.column, xAxis.tickStrategy, xAxis.type]);

    const { theme, margin, yAxii } = useMemo(() => {
        const lineStyles = { strokeWidth: 1, stroke: 'var(--color-surface-muted)' };
        return {
            theme: createTheme({
                xAxisLineStyles: lineStyles,
                yAxisLineStyles: lineStyles,
                gridStyles: lineStyles,
                xTickLineStyles: lineStyles,
                yTickLineStyles: lineStyles,
            }),
            yAxii: uniqBy(
                groups.filter(({ axis }) => axis.orientation !== 'none'),
                'axis.orientation',
            ),
            margin: calculateMargin({
                groups,
                isSmallArea,
                legend: dynamicLegend,
                xAxis,
                brush: brushEnabled,
                canShowLegend,
                tickLength,
                tickWidth,
            }),
        };
    }, [dynamicLegend, isSmallArea, groups, xAxis, brushEnabled, canShowLegend, tickLength, tickWidth]);

    const tickFormatter = useEvent((value: any) => {
        if (xAxis.type === 'date') {
            let formatType = params?.timeframe ?? 'days';
            if (params?.timeframe === 'days') {
                if (filteredData.length > 365) {
                    formatType = 'weeks';
                }
            }
            return formatTick(value, formatType);
        }
        return value;
    });

    const activeFrameSize = filteredData.length;
    const dynamicStrokeWidth = useMemo(() => {
        const shrinkFactor = Math.max(Math.log10(activeFrameSize) - 1.5, 0);
        return Math.max(strokeWidth - shrinkFactor, 0.75);
    }, [strokeWidth, activeFrameSize]);

    const dynamicScatterSize = useMemo(() => {
        const scale = activeFrameSize / 100;
        const glyphSize = Math.max(8 * (1 / scale), 3);
        return glyphSize > 16 ? 16 : glyphSize;
    }, [activeFrameSize]);

    const brushOptions = useMemo(() => ({ ...options, legend: dynamicLegend }), [dynamicLegend, options]);
    const isVisible = width > 0 && height > 0;
    return (
        <TooltipProvider hideTooltipDebounceMs={0}>
            <DataProvider
                theme={theme}
                xScale={{ type: 'band', reverse, ...xScaleConfig }}
                yScale={{ round: true, nice: params?.transform !== 'percent', type: logarithmic ? 'log' : 'linear' }}
            >
                <Bit.div ref={containerRef} w="full" h="full" display="flex" pos="relative" overflow="visible">
                    {isVisible && (
                        <Bit.div style={{ height: `${height}px`, width: `${width}px` }}>
                            <XYChart width={width} height={height} margin={margin}>
                                <Grid columns={false} numTicks={numYTicks} />
                                {groups.map(({ name, type, stack, series, id }) => {
                                    const Chart = TimeseriesComponents[type as keyof typeof TimeseriesComponents];
                                    const groupKey = `${vizId}-${name ?? id}-${type}`;
                                    const visibleSeries = series.filter(s => !hidden.includes(s.column as keyof T));
                                    if (!Chart) return null;
                                    const renderedSeries = series.map(seriesConfig => {
                                        const color = d3Color.color(seriesConfig.color);
                                        const isFaded = highlight && highlight !== seriesConfig.column;
                                        const isVisible = !hidden.includes(seriesConfig.column as keyof T);
                                        const baseOpacity = ['bar', 'area'].includes(type) ? 0.75 : 1;
                                        const opacity = isFaded ? 0.25 : baseOpacity;

                                        const adjustedColor = color?.copy({ opacity: opacity }).formatRgb();
                                        const colorProps =
                                            type === 'area'
                                                ? {
                                                      fill: adjustedColor,
                                                      stroke: adjustedColor,
                                                      strokeWidth: 0,
                                                      lineProps: { stroke: 'transparent', strokeWidth: 0 },
                                                  }
                                                : { colorAccessor: () => adjustedColor };

                                        const scatterProps = type === 'scatter' ? { size: dynamicScatterSize } : {};
                                        return (
                                            <Chart
                                                key={`${groupKey}-${String(seriesConfig.column)}`}
                                                data={parsedData}
                                                xAccessor={row => {
                                                    const xValue = row[xAxis.column];
                                                    return xValue;
                                                }}
                                                dataKey={String(seriesConfig.column)}
                                                yAccessor={row => {
                                                    const yValue = !isVisible ? undefined : row[seriesConfig.column];
                                                    return yValue;
                                                }}
                                                strokeWidth={dynamicStrokeWidth}
                                                {...colorProps}
                                                {...scatterProps}
                                            />
                                        );
                                    });

                                    if (type === 'bar') {
                                        if (stack && visibleSeries.length >= 1) {
                                            return (
                                                <BarStack key={groupKey.concat('-bar-stack')}>
                                                    {renderedSeries}
                                                </BarStack>
                                            );
                                        }
                                        if (visibleSeries.length >= 1) {
                                            return (
                                                <BarGroup key={groupKey.concat('-bar-group')}>
                                                    {renderedSeries}
                                                </BarGroup>
                                            );
                                        }
                                        return renderedSeries;
                                    }

                                    if (type === 'area') {
                                        if (stack) {
                                            return (
                                                <AreaStack renderLine={false} key={groupKey.concat('-area-stack')}>
                                                    {renderedSeries}
                                                </AreaStack>
                                            );
                                        }
                                    }

                                    return renderedSeries;
                                })}

                                <Axis
                                    orientation="bottom"
                                    tickFormat={x => {
                                        if (x === undefined) return undefined;
                                        return tickFormatter(x);
                                    }}
                                    label={xAxis.title}
                                    labelOffset={xAxis.tickStrategy === 'all' ? tickLength + 16 : 16}
                                    numTicks={numXTicks}
                                    tickLabelProps={() => {
                                        return xAxis.tickStrategy === 'all'
                                            ? {
                                                  y: 8,
                                                  fontSize: 8,
                                                  angle: -45,
                                                  textAnchor: 'end',
                                                  className: `${TICK_LABEL_CLASSNAME} fill-content-muted select-none`,
                                              }
                                            : {
                                                  y: 16,
                                                  fontSize: 10,
                                                  textAnchor: 'middle',
                                                  className: `${TICK_LABEL_CLASSNAME} fill-content-muted select-none`,
                                              };
                                    }}
                                />
                                {yAxii.map(({ axis, ...scale }) => {
                                    const orientation = axis?.orientation ?? 'none';
                                    if (!axis || orientation === 'none' || !scale) return null;
                                    return (
                                        <Axis
                                            key={`${vizId}-y-axis-${axis.title}-${axis.orientation}-${axis.format}`}
                                            orientation={orientation}
                                            label={axis.title}
                                            tickFormat={scale.tickFormat}
                                            numTicks={numYTicks}
                                            labelOffset={32}
                                            tickLabelProps={{
                                                fontSize: 10,
                                                className: `select-none translate-y-0.5 ${TICK_LABEL_CLASSNAME} fill-content-muted`,
                                            }}
                                            labelClassName=" fill-content-muted"
                                        />
                                    );
                                })}
                                <VisualizationTooltip<T>
                                    visualizationId={vizId}
                                    tooltip={tooltip}
                                    meta={meta}
                                    xAxis={xAxis}
                                    groups={groups}
                                    params={params}
                                    hidden={hidden}
                                />
                            </XYChart>
                            {brushEnabled && (
                                <BrushArea<T>
                                    hidden={hidden}
                                    height={BRUSH_HEIGHT}
                                    visualizationId={vizId}
                                    onChange={setBrushBounds}
                                    options={brushOptions}
                                    data={orderedData}
                                    groups={rawGroups}
                                    interval={activeIntervalUnit}
                                />
                            )}
                        </Bit.div>
                    )}
                    {isVisible && canShowLegend && dynamicLegend?.position && dynamicLegend?.position !== 'none' && (
                        <Legend
                            hidden={hidden}
                            onHide={setHidden}
                            highlight={highlight}
                            onHighlight={setHighlight}
                            position={dynamicLegend?.position}
                            groups={rawGroups}
                        />
                    )}
                </Bit.div>
            </DataProvider>
        </TooltipProvider>
    );
}
